import IconSubmit from '@assets/icons/check.svg?react';
import {
 Box,
 Button,
 Dialog,
 DialogContent,
 DialogTitle,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
 useMediaQuery,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
 Dispatch,
 MutableRefObject,
 SetStateAction,
 useEffect,
 useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CloseButton from '../CloseButton';
import SelectApi from '../SelectApi';
import XTabs from '../XTabs';
import ChangePassword from '@/components/adminUsers/ChangePassword';
import GenerateAPIKey from '@/components/adminUsers/GenerateAPIKey';
import {
 AccessGroupsDTO,
 GroupDTO,
 UserCustomerDTO,
 UsersDTO,
} from '@redux/identity/identityTypes';
import {
 createItemStart,
 getItemStart,
 updateItemStart,
 usersGetSelfAccessGroupsStart,
 usersGetUserGroupsStart,
} from '@redux/users/usersSlice';
import { RootState } from '@redux/store';
import themeOptions from '../../../utils/theme';
import { CustomGridApi, TableModalState } from '../XTable/types';
import { NO_MODAL_ID } from '../XTable/constants';
import { UserAccessGroupsSection } from '../InfoSidebar/UserAccessGroupsSection';
import { UserSettingsSwitches } from '../InfoSidebar/UserIdentitySections';

const defaultWidth = { xs: '100%', md: '410px' };
const requestPasswordOptions = [14, 30, 60, 90, 120];
const requestPasswordLabelId = 'request-password-days-label';

interface UserFormValues {
 Name: string;
 Surname: string;
 AuthEmail: string;
 AuthPhone: string;
 Customer: UserCustomerDTO | null;
 RequestToChangePasswordAfterDays: number | '';
}

interface PasswordFormState {
 password: string;
 repeatPassword: string;
 hasMismatch: boolean;
 isCheckingPasswords: boolean;
}

const defaultValues: UserFormValues = {
 Name: '',
 Surname: '',
 AuthEmail: '',
 AuthPhone: '',
 Customer: null,
 RequestToChangePasswordAfterDays: '',
};

const UserEditModal = ({
 setCreateModalOpen,
 isOpen,
 modalState,
 setModalState,
 apiRef,
}: {
 setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
 isOpen: boolean;
 modalState?: TableModalState;
 setModalState: Dispatch<SetStateAction<TableModalState>>;
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));
 const { item, user, groups, accessGroups, loading, success } = useSelector(
  (state: RootState) => state.users,
 );

 const isUpdate = modalState?.action === 'update';
 const activeUserId =
  isUpdate && item?.Id ? item.Id : isUpdate ? String(modalState?.id || '') : '';

 const { control, handleSubmit, reset, setValue, getValues } =
  useForm<UserFormValues>({
   defaultValues,
  });

 const [userGroupIds, setUserGroupIds] = useState<number[]>([]);
 const [userAccessGroupIds, setUserAccessGroupIds] = useState<string[]>([]);
 const [lockoutEnabled, setLockoutEnabled] = useState(false);
 const [twoFAEnabled, setTwoFAEnabled] = useState(false);
 const [passwordState, setPasswordState] = useState<PasswordFormState>({
  password: '',
  repeatPassword: '',
  hasMismatch: false,
  isCheckingPasswords: false,
 });

 useEffect(() => {
  if (!isOpen) {
   return;
  }
  dispatch(usersGetUserGroupsStart({ limit: 1000 }));
  dispatch(usersGetSelfAccessGroupsStart());
  if (isUpdate && modalState?.id) {
   dispatch(getItemStart({ table: 'users', id: modalState.id }));
   return;
  }
  reset({
   ...defaultValues,
   Customer: user?.Customer || null,
  });
  setUserGroupIds([]);
  setUserAccessGroupIds([]);
  setLockoutEnabled(false);
  setTwoFAEnabled(false);
  setPasswordState({
   password: '',
   repeatPassword: '',
   hasMismatch: false,
   isCheckingPasswords: false,
  });
 }, [dispatch, isOpen, isUpdate, modalState?.id, reset, user?.Customer]);

 useEffect(() => {
  if (!isOpen || isUpdate) {
   return;
  }
  const currentCustomer = getValues('Customer');
  if (currentCustomer) {
   return;
  }
  if (!user?.Customer) {
   return;
  }
  setValue('Customer', user.Customer);
 }, [getValues, isOpen, isUpdate, setValue, user?.Customer]);

 useEffect(() => {
  if (!isOpen || !isUpdate || !item?.Id || !modalState?.id) {
   return;
  }
  if (String(item.Id) !== String(modalState.id)) {
   return;
  }
  reset({
   Name: item.Name || '',
   Surname: item.Surname || '',
   AuthEmail: item.AuthEmail || '',
   AuthPhone: item.AuthPhone || '',
   Customer: item.Customer || null,
   RequestToChangePasswordAfterDays:
    item.RequestToChangePasswordAfterDays || '',
  });
  setUserGroupIds((item.UserGroups || []).map((group: GroupDTO) => group.Id));
  setUserAccessGroupIds(
   (item.AccessGroups || [])
    .map((accessGroup: AccessGroupsDTO) => accessGroup.Id || '')
    .filter((id: string) => !!id),
  );
  setLockoutEnabled(!!item.LockoutEndDate);
  setTwoFAEnabled(!!item.TwoFAEnabled);
  setPasswordState({
   password: '',
   repeatPassword: '',
   hasMismatch: false,
   isCheckingPasswords: false,
  });
 }, [isOpen, isUpdate, item, modalState?.id, reset]);

 useEffect(() => {
  if (!isOpen) {
   return;
  }
  const saveSucceeded = success?.createItem || success?.updateItem;
  if (!saveSucceeded) {
   return;
  }
  handleClose();
  apiRef.current.fetchData?.();
 }, [apiRef, isOpen, success?.createItem, success?.updateItem]);

 const handleClose = () => {
  setModalState({ id: NO_MODAL_ID, action: 'create' });
  setCreateModalOpen(false);
 };

 const handleGroupToggle = (groupId: number, isChecked: boolean) => {
  if (isChecked) {
   setUserGroupIds((prevValue) =>
    prevValue.includes(groupId) ? prevValue : [...prevValue, groupId],
   );
   return;
  }
  setUserGroupIds((prevValue) => prevValue.filter((id) => id !== groupId));
 };

 const handleAccessGroupToggle = (
  accessGroupId: string,
  isChecked: boolean,
 ) => {
  if (isChecked) {
   setUserAccessGroupIds((prevValue) =>
    prevValue.includes(accessGroupId)
     ? prevValue
     : [...prevValue, accessGroupId],
   );
   return;
  }
  setUserAccessGroupIds((prevValue) =>
   prevValue.filter((id) => id !== accessGroupId),
  );
 };

 const handleFormSubmit = (values: UserFormValues) => {
  const hasPasswordInput =
   passwordState.password.length > 0 || passwordState.repeatPassword.length > 0;
  if (hasPasswordInput) {
   const isPasswordInvalid =
    passwordState.hasMismatch ||
    passwordState.isCheckingPasswords ||
    passwordState.password.length === 0 ||
    passwordState.repeatPassword.length === 0;
   if (isPasswordInvalid) {
    return;
   }
  }

  const lockoutEndDate = lockoutEnabled
   ? new Date(
      new Date().setFullYear(new Date().getFullYear() + 100),
     ).toISOString()
   : null;

  const payload: UsersDTO = {
   ...(isUpdate ? item || {} : {}),
   Name: values.Name || '',
   Surname: values.Surname || '',
   AuthEmail: values.AuthEmail || '',
   AuthPhone: values.AuthPhone || '',
   Customer: values.Customer || null,
   RequestToChangePasswordAfterDays:
    values.RequestToChangePasswordAfterDays === ''
     ? null
     : values.RequestToChangePasswordAfterDays,
   LockoutEnabled: lockoutEnabled,
   LockoutEndDate: lockoutEndDate,
   TwoFAEnabled: twoFAEnabled,
   UserGroups: userGroupIds.map((id) => ({ Id: id })),
   AccessGroups: userAccessGroupIds.map((id) => ({ Id: id })),
   NewPassword: hasPasswordInput ? passwordState.password : undefined,
  };

  if (isUpdate && activeUserId) {
   dispatch(
    updateItemStart({
     table: 'users',
     id: activeUserId,
     data: payload,
     isPut: true,
    }),
   );
   return;
  }

  dispatch(
   createItemStart({
    table: 'users',
    data: payload,
   }),
  );
 };

 const isSubmitting = !!loading?.createItem || !!loading?.updateItem;
 const isSaveDisabled =
  isSubmitting ||
  passwordState.isCheckingPasswords ||
  passwordState.hasMismatch ||
  (passwordState.password.length > 0 &&
   passwordState.repeatPassword.length === 0) ||
  (passwordState.repeatPassword.length > 0 &&
   passwordState.password.length === 0);

 const generalTab = (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
   <Controller
    name="Name"
    control={control}
    render={({ field }) => (
     <TextField {...field} label={t('user.fields.name')} fullWidth />
    )}
   />
   <Controller
    name="Surname"
    control={control}
    render={({ field }) => (
     <TextField {...field} label={t('user.fields.surname')} fullWidth />
    )}
   />
   <Controller
    name="AuthEmail"
    control={control}
    rules={{ required: true }}
    render={({ field }) => (
     <TextField {...field} label={t('user.fields.authEmail')} fullWidth />
    )}
   />
   <Controller
    name="AuthPhone"
    control={control}
    render={({ field }) => (
     <TextField {...field} label={t('user.fields.authPhone')} fullWidth />
    )}
   />
   <Controller
    name="Customer"
    control={control}
    render={({ field }) => (
     <SelectApi
      {...field}
      isEditable
      operator="contains"
      url="/api/Customers/v2/"
      filter='{"fields":"Id,Name"}'
      label={t('user.fields.customer')}
      control={control}
      setValue={setValue}
     />
    )}
   />

   <Controller
    name="RequestToChangePasswordAfterDays"
    control={control}
    render={({ field }) => (
     <FormControl fullWidth>
      <InputLabel id={requestPasswordLabelId}>
       {t('user.fields.requestToChangePasswordAfterDays')}
      </InputLabel>
      <Select
       {...field}
       labelId={requestPasswordLabelId}
       label={t('user.fields.requestToChangePasswordAfterDays')}
       value={field.value}
      >
       {requestPasswordOptions.map((option) => (
        <MenuItem key={option} value={option}>
         {option}
        </MenuItem>
       ))}
      </Select>
     </FormControl>
    )}
   />
   {activeUserId && item && <GenerateAPIKey user={item} />}
   <UserSettingsSwitches
    lockoutEnabled={lockoutEnabled}
    twoFAEnabled={twoFAEnabled}
    onLockoutToggle={setLockoutEnabled}
    onTwoFAToggle={setTwoFAEnabled}
    disabled={isSubmitting}
   />
  </Box>
 );

 const additionalTab = (
  <Box sx={{ p: 0 }}>
   <ChangePassword
    item={item || { Id: activeUserId }}
    hideButtons
    isSubmittingOverride={isSubmitting}
    onStateChange={setPasswordState}
   />
   <UserAccessGroupsSection
    groups={groups}
    accessGroups={accessGroups}
    selectedGroupIds={userGroupIds}
    selectedAccessGroupIds={userAccessGroupIds}
    onGroupToggle={handleGroupToggle}
    onAccessGroupToggle={handleAccessGroupToggle}
    loadingGroups={!!loading?.usersGetUserGroups}
    loadingAccessGroups={!!loading?.usersGetSelfAccessGroups}
    disabled={isSubmitting}
   />
  </Box>
 );

 const tabs = [
  { id: 'general', label: t('entities.data'), content: generalTab },
  { id: 'additional', label: t('entities.additional'), content: additionalTab },
 ];

 return (
  <Dialog
   open={isOpen}
   onClose={handleClose}
   fullScreen={!isLargerThanMd}
   maxWidth="lg"
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': {
      maxWidth: defaultWidth,
     },
    },
    '& .MuiDialog-paper': {
     overflow: 'hidden',
    },
   }}
  >
   <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
     <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
      {isUpdate ? t('global.edit') : t('global.create')}
     </Typography>
    </DialogTitle>
    <CloseButton closeDialog={handleClose} />
    <DialogContent
     sx={{
      paddingTop: 0,
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      width: defaultWidth,
      height: '100%',
      flexDirection: 'column',
     }}
    >
     <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <XTabs tabs={tabs} ariaLabel={t('user.title')} keepMounted />
     </Box>
     <Box sx={{ p: 2, pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
       type="button"
       onClick={() => handleSubmit(handleFormSubmit)()}
       variant="contained"
       color="primary"
       disabled={isSaveDisabled}
       endIcon={
        <IconSubmit style={{ color: 'white', width: '16px', height: '11px' }} />
       }
      >
       {isUpdate ? t('global.save') : t('global.create')}
      </Button>
     </Box>
    </DialogContent>
   </LocalizationProvider>
  </Dialog>
 );
};

export default UserEditModal;
